/**
 * StatusBadge — single source of truth for expense / fund-transaction status pills.
 *
 * Derives every visual badge from the entity in one place so the same expense
 * looks identical everywhere it appears (Approvals, GroupDetail Expenses tab,
 * Fund tab, etc.).
 *
 * Usage:
 *   <StatusBadge expense={expense} />              // standard (max 2 visible + overflow)
 *   <StatusBadge expense={expense} verbose />      // shows ALL applicable badges
 *   <StatusBadge expense={expense} compact />      // tiny, icon-only
 *   <StatusBadge fundTxn={txn} />                  // fund transaction variant
 */
import {
  Clock,
  Users,
  Check,
  X,
  Wallet,
  RotateCcw,
  Repeat,
  AlertCircle,
} from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from './tooltip';

/* ---- Badge token registry ---- */
const TOKENS = {
  pending_admin: {
    label: 'Pending Admin',
    short: 'Admin',
    icon: Clock,
    classes: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/40',
    tooltip: 'Awaiting approval from a group admin.',
  },
  awaiting_peer: {
    label: 'Awaiting Peer',
    short: 'Peer',
    icon: Users,
    classes: 'bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-500/40',
    tooltip: 'A 1-on-1 expense — waiting for the other person to approve.',
  },
  pending_members: {
    label: 'Pending Members',
    short: 'Members',
    icon: Users,
    classes: 'bg-sky-500/20 text-sky-700 dark:text-sky-400 border-sky-500/40',
    tooltip: 'Split members vote to approve this expense (group policy).',
  },
  approved: {
    label: 'Approved',
    short: 'Approved',
    icon: Check,
    classes: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/40',
    tooltip: 'Expense approved and balances updated.',
  },
  rejected: {
    label: 'Rejected',
    short: 'Rejected',
    icon: X,
    classes: 'bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/40',
    tooltip: 'Expense was rejected.',
  },
  reversed: {
    label: 'Reversed',
    short: 'Reversed',
    icon: RotateCcw,
    classes: 'bg-gray-500/25 text-gray-600 dark:text-gray-400 border-gray-500/40 line-through',
    tooltip: 'This entry was reversed.',
  },
  from_fund: {
    label: 'From Fund',
    short: 'Fund',
    icon: Wallet,
    classes: 'bg-sky-500/20 text-sky-700 dark:text-sky-400 border-sky-500/40',
    tooltip: 'Paid from the group fund (no individual split).',
  },
  recurring: {
    label: 'Recurring',
    short: 'Recurring',
    icon: Repeat,
    classes: 'bg-violet-500/20 text-violet-700 dark:text-violet-400 border-violet-500/40',
    tooltip: 'Auto-generated from a recurring expense schedule.',
  },
  edited: {
    label: 'Edited',
    short: 'Edited',
    icon: AlertCircle,
    classes: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/40',
    tooltip: 'This expense was edited after creation.',
  },
};

/* ---- Derive applicable tokens ---- */
function deriveExpenseTokens(expense) {
  if (!expense) return [];
  const tokens = [];

  // Status
  if (expense.reversed) {
    tokens.push('reversed');
  } else if (expense.status === 'approved') {
    tokens.push('approved');
  } else if (expense.status === 'rejected') {
    tokens.push('rejected');
  } else if (expense.status === 'pending') {
    if (expense.approval_type === 'peer') {
      tokens.push('awaiting_peer');
    } else if (expense.approval_type === 'members') {
      tokens.push('pending_members');
    } else {
      tokens.push('pending_admin');
    }
  }

  // Modifiers
  if (expense.paid_by === 'group_fund') tokens.push('from_fund');
  if (expense.is_recurring || expense.recurring_id) tokens.push('recurring');
  if (expense.edited_at) tokens.push('edited');

  return tokens;
}

function deriveFundTxnTokens(txn) {
  if (!txn) return [];
  const tokens = [];
  if (txn.reversed || (txn.type && String(txn.type).includes('reversal'))) {
    tokens.push('reversed');
  }
  return tokens;
}

/* ---- Single pill ---- */
function Pill({ tokenKey, compact = false, withTooltip = true, testId }) {
  const t = TOKENS[tokenKey];
  if (!t) return null;
  const Icon = t.icon;
  const label = compact ? null : t.label;

  const pill = (
    <span
      data-testid={testId || `status-badge-${tokenKey}`}
      className={`inline-flex items-center gap-1 ${compact ? 'px-1.5 py-0.5' : 'px-2 py-0.5'} rounded-full border text-xs font-bold uppercase tracking-wider whitespace-nowrap ${t.classes}`}
    >
      <Icon className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} strokeWidth={2.5} />
      {label && <span>{label}</span>}
    </span>
  );

  if (!withTooltip) return pill;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{pill}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-center">
        <div className="font-bold text-xs mb-0.5">{t.label}</div>
        <div className="text-xs opacity-90 font-normal">{t.tooltip}</div>
      </TooltipContent>
    </Tooltip>
  );
}

/* ---- Public component ---- */
export default function StatusBadge({
  expense,
  fundTxn,
  compact = false,
  verbose = false,
  maxVisible = 2,
  className = '',
  withTooltip = true,
}) {
  const tokens = expense ? deriveExpenseTokens(expense) : deriveFundTxnTokens(fundTxn);
  if (tokens.length === 0) return null;

  const visible = verbose ? tokens : tokens.slice(0, maxVisible);
  const overflow = verbose ? 0 : Math.max(0, tokens.length - maxVisible);
  const hiddenTokens = verbose ? [] : tokens.slice(maxVisible);

  return (
    <div className={`inline-flex flex-wrap items-center gap-1 ${className}`} data-testid="status-badge-group">
      {visible.map((k) => (
        <Pill key={k} tokenKey={k} compact={compact} withTooltip={withTooltip} />
      ))}
      {overflow > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              data-testid="status-badge-overflow"
              className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-border bg-muted text-xs font-bold text-muted-foreground"
            >
              +{overflow}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {hiddenTokens.map((k) => TOKENS[k]?.label).filter(Boolean).join(' · ')}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export { TOKENS as STATUS_TOKENS };
